/// <reference types="bun" />
/**
 * Cliente unico da API do Orbind — usado por web, mobile e desktop.
 *
 * A referencia a `bun` acima nao e opcional: importar `AppType` traz o grafo de
 * modulos de `apps/api` para dentro do programa de tipos de quem consome este
 * pacote, e o servidor usa globais do Bun (`Bun.password`). Sem ela, o
 * typecheck da web e do mobile quebra em codigo que eles nem executam.
 *
 * Construido sobre o RPC do Hono: o tipo `AppType` vem direto de `apps/api`,
 * entao renomear uma rota ou mudar um payload quebra a compilacao das tres
 * plataformas de uma vez, em vez de falhar em runtime numa delas so.
 *
 * O que muda entre plataformas e apenas o `TokenStorage` (localStorage na web,
 * SecureStore no mobile, store criptografado do Tauri no desktop).
 */
import type { AppType } from '@orbind/api/src/app.ts'
import { hc } from 'hono/client'

export type { AppType }

/**
 * Onde o token de sessao e guardado. Sincrono de proposito: o cliente precisa
 * do token no momento de montar o header, e um await ali transformaria toda
 * chamada numa cadeia de promessas so por causa do storage.
 */
export type TokenStorage = {
  get(): string | null
  set(token: string | null): void
}

export function memoryTokenStorage(initial: string | null = null): TokenStorage {
  let token = initial
  return { get: () => token, set: (value) => void (token = value) }
}

export type ApiErrorPayload = { code: string; message: string; details?: unknown }

/** Erro tipado da API, com o `code` estavel que a UI traduz. */
export class OrbindApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: unknown

  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.message)
    this.name = 'OrbindApiError'
    this.status = status
    this.code = payload.code
    this.details = payload.details
  }
}

export type CreateClientOptions = {
  baseUrl: string
  storage: TokenStorage
  /** Chamado quando a API responde 401 — a UI derruba a sessao e volta ao login. */
  onUnauthorized?: () => void
  /** Idioma corrente, enviado em Accept-Language para o fallback do servidor. */
  getLocale?: () => string
}

export function createOrbindClient({ baseUrl, storage, onUnauthorized, getLocale }: CreateClientOptions) {
  const client = hc<AppType>(baseUrl, {
    fetch: async (input: RequestInfo | URL, init?: RequestInit) => {
      const headers = new Headers(init?.headers)
      const token = storage.get()
      if (token) headers.set('Authorization', `Bearer ${token}`)
      const locale = getLocale?.()
      if (locale) headers.set('Accept-Language', locale)

      const response = await fetch(input, { ...init, headers })

      // "Manter conectado": o servidor renovou a sessão e mandou o token novo
      // (SPEC-20261022-1004-manter-conectado-30-dias)
      const renovado = response.ok ? response.headers.get('X-Orbind-Token') : null
      if (renovado) storage.set(renovado)

      if (response.status === 401) {
        storage.set(null)
        onUnauthorized?.()
      }
      if (!response.ok) {
        // Corpo de erro sempre no formato { error: { code, message, details } }.
        const body = (await response.clone().json().catch(() => null)) as { error?: ApiErrorPayload } | null
        throw new OrbindApiError(
          response.status,
          /*
           * `http_error`, e não `network_error` (SPEC-20261001-0948): chegou
           * uma RESPOSTA, com status. Chamar isso de erro de rede manda quem
           * lê conferir a conexão em vez de olhar o status — foi exatamente o
           * que aconteceu no formulário de convite.
           */
          body?.error ?? {
            code: 'http_error',
            message: response.statusText || `O servidor respondeu ${response.status}.`,
          },
        )
      }
      return response
    },
  })

  // A API vive sob /api no servidor (o primeiro nivel fica para as rotas do
  // SPA, servido pelo mesmo processo em producao). O prefixo ja vem no
  // AppType, entao expomos o sub-objeto e as telas seguem chamando
  // `api.kanbans`, `api['work-items']`… sem saber dele.
  const routes = client.api

  const helpers = {
    /**
     * Faz login e ja persiste o token no storage da plataforma.
     * Conta sem workspace responde `needsWorkspace` com token nulo
     * (SPEC-20260814-2128) — nesse caso nada e gravado.
     */
    async login(email: string, password: string, manterConectado = false) {
      const response = await routes.auth.login.$post({ json: { email, password, manterConectado } })
      const data = await response.json()
      if (data.token) storage.set(data.token)
      return data
    },
    logout() {
      storage.set(null)
    },
    get isAuthenticated() {
      return storage.get() !== null
    },
  }

  // O `hc` devolve um Proxy que resolve rotas dinamicamente; um spread
  // (`{...client}`) enumeraria as own keys — que um Proxy nao reporta — e
  // perderia TODAS as rotas. Compomos com outro Proxy em vez de espalhar.
  return new Proxy(routes, {
    get(target, prop, receiver) {
      if (prop in helpers) return Reflect.get(helpers, prop)
      return Reflect.get(target, prop, receiver)
    },
  }) as typeof routes & typeof helpers
}

export type OrbindClient = ReturnType<typeof createOrbindClient>
